import React, { useState, useEffect } from 'react';
import { ElectricityPrice, PriceArea } from '../types';
import { parseISO, format } from 'date-fns';
import { nb } from 'date-fns/locale';
import { historicalPrices } from '../utils/api';
import { Check, ArrowLeft, TrendingUp, TrendingDown } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { calculateElectricitySubsidy, getNorgesprisPrice, getVatMultiplier } from '../utils/subsidyCalculator';
import { Link } from 'react-router-dom';
import { LineChart, BarChart, ArrowUpDown, ArrowRight } from 'lucide-react';

const NORGESPRIS_COLOR = '#f97316';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface PriceChartProps {
  prices: ElectricityPrice[];
  area: PriceArea;
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

const PriceChart: React.FC<PriceChartProps> = ({ prices, area, selectedDate, onDateChange }) => {
  const [chartType, setChartType] = useState<ChartType>('line');
  const [showSubsidy, setShowSubsidy] = useState<boolean>(true);
  const [showHistorical, setShowHistorical] = useState<boolean>(false);
  const [showNorgespris, setShowNorgespris] = useState<boolean>(true);
  const [blink, setBlink] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const interval = setInterval(() => {
      setBlink((prev) => !prev);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (prices.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-500">Ingen prisdata tilgjengelig</p>
      </div>
    );
  }

  // Find the current hour
  const now = new Date();
  const currentHourIndex = prices.findIndex(
    (price) =>
      parseISO(price.time_start) <= now &&
      parseISO(price.time_end) > now
  );

  const labels = prices.map((price) =>
    format(parseISO(price.time_start), 'HH:mm', { locale: nb })
  );

  const priceData = prices.map((price) => price.NOK_per_kWh);

  // Calculate prices after subsidy
  const pricesAfterSubsidy = prices.map((price) => {
    const subsidy = calculateElectricitySubsidy(price.NOK_per_kWh, area);
    return subsidy.isSubsidyActive ? subsidy.priceAfterSubsidy : price.NOK_per_kWh;
  });

  // Only show subsidy line if there are any subsidized prices
  const hasSubsidizedPrices = prices.some(price => 
    calculateElectricitySubsidy(price.NOK_per_kWh, area).isSubsidyActive
  );

  // Find min and max prices
  const maxPrice = Math.max(...priceData);
  const minPrice = Math.min(...priceData);

  // Compare with Norgespris (same VAT basis as the shown prices)
  const norgespris = getNorgesprisPrice(area);
  const hoursAboveNorgespris = priceData.filter((price) => price > norgespris).length;
  const currentPrice = currentHourIndex >= 0 ? priceData[currentHourIndex] : null;
  const currentDiff = currentPrice !== null ? currentPrice - norgespris : null;
  const isAboveNorgespris = currentDiff !== null ? currentDiff > 0 : hoursAboveNorgespris > prices.length / 2;

  // Calculate average
  const avgPrice =
    priceData.reduce((sum, price) => sum + price, 0) / priceData.length;

  // Average for the same month last year (øre excl. VAT -> NOK, same VAT basis as the shown prices)
  const currentMonth = format(selectedDate, 'MMMM') as keyof typeof historicalPrices;
  const historicalAverage = (historicalPrices[currentMonth]?.[area] / 100) * getVatMultiplier(area);
  const lastYearLabel = `Snitt ${format(selectedDate, 'MMMM', { locale: nb })} i fjor`;

  // Calculate price changes
  const priceChanges = priceData.map(
    (price) => ((price - avgPrice) / avgPrice) * 100
  );

  // Identify cheap/expensive hours
  const cheapHours = prices
    .filter((price) => price.NOK_per_kWh < avgPrice * 0.9)
    .map((price) => format(parseISO(price.time_start), 'HH:mm', { locale: nb }));

  const expensiveHours = prices
    .filter((price) => price.NOK_per_kWh > avgPrice * 1.1)
    .map((price) => format(parseISO(price.time_start), 'HH:mm', { locale: nb }));

  // Find the best 3-hour period for electricity usage
  let bestBlock = { startIndex: 0, endIndex: 2, avgPrice: Number.MAX_VALUE };

  for (let i = 0; i <= prices.length - 3; i++) {
    const blockPrices = prices.slice(i, i + 3);
    const avgPrice = blockPrices.reduce((sum, p) => sum + p.NOK_per_kWh, 0) / blockPrices.length;

    if (avgPrice < bestBlock.avgPrice) {
      bestBlock = { startIndex: i, endIndex: i + 2, avgPrice };
    }
  }

  // Extract the actual time periods
  const bestStartTime = parseISO(prices[bestBlock.startIndex].time_start);
  const bestEndTime = parseISO(prices[bestBlock.endIndex].time_end);

  const data = {
    labels,
    datasets: [
      {
        label: 'Originalpris (NOK/kWh)',
        data: priceData,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.05)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: (context: any) => {
          const index = context.dataIndex;
          if (index === currentHourIndex) {
            return blink
              ? 'rgb(112, 41, 99)'
              : 'rgba(112, 41, 99, 0)';
          }
          return 'rgb(59, 130, 246)';
        },
        pointRadius: (context: any) => {
          const index = context.dataIndex;
          if (index === currentHourIndex) return isMobile ? 7 : 6;
          return isMobile ? 2 : 3;
        },
        pointBorderWidth: (context: any) => {
          const index = context.dataIndex;
          return index === currentHourIndex ? 2 : 1;
        },
      },
      ...(showSubsidy && hasSubsidizedPrices
        ? [
            {
              label: 'Pris etter strømstøtte',
              data: pricesAfterSubsidy,
              borderColor: '#22c55e',
              borderDash: [3, 3],
              backgroundColor: 'transparent',
              tension: 0.4,
              pointRadius: 0,
            },
          ]
        : []),
      ...(showHistorical && historicalAverage
        ? [
            {
              label: lastYearLabel,
              data: Array(prices.length).fill(historicalAverage),
              borderColor: 'rgb(234, 179, 8)',
              borderDash: [5, 5],
              borderWidth: 2,
              pointRadius: 0,
              tension: 0,
            },
          ]
        : []),
      ...(showNorgespris
        ? [
            {
              label: `Norgespris (${norgespris.toFixed(2)} kr)`,
              data: Array(prices.length).fill(norgespris),
              borderColor: NORGESPRIS_COLOR,
              backgroundColor: 'transparent',
              borderWidth: 2.5,
              pointRadius: 0,
              tension: 0,
              type: 'line' as const,
            },
          ]
        : []),
    ],
  };

  const barData = {
    labels,
    datasets: [
      {
        label: 'Originalpris (NOK/kWh)',
        data: priceData,
        backgroundColor: '#3b82f6',
        borderColor: '#3b82f6',
        borderWidth: (context: any) => {
          const index = context.dataIndex;
          return index === currentHourIndex ? 2 : 1;
        }
      },
      ...(showSubsidy && hasSubsidizedPrices
        ? [
            {
              label: 'Pris etter strømstøtte',
              data: pricesAfterSubsidy,
              backgroundColor: 'transparent',
              borderColor: '#22c55e',
              borderWidth: 2,
              type: 'line' as const,
            },
          ]
        : []),
      ...(showNorgespris
        ? [
            {
              label: `Norgespris (${norgespris.toFixed(2)} kr)`,
              data: Array(prices.length).fill(norgespris),
              borderColor: NORGESPRIS_COLOR,
              backgroundColor: 'transparent',
              borderWidth: 2.5,
              pointRadius: 0,
              tension: 0,
              type: 'line' as const,
            },
          ]
        : []),
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    hover: {
      mode: 'nearest',
      intersect: false,
      axis: 'x'
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          boxWidth: 12,
          padding: 15,
          font: {
            size: isMobile ? 11 : 12
          }
        }
      },
      tooltip: {
        mode: 'nearest',
        intersect: false,
        axis: 'x',
        titleFont: {
          size: isMobile ? 14 : 12
        },
        bodyFont: {
          size: isMobile ? 13 : 12
        },
        callbacks: {
          label: function(context: any) {
            const datasetIndex = context.datasetIndex;
            const index = context.dataIndex;
            
            // If this is the original price dataset
            if (datasetIndex === 0) {
              const originalPrice = priceData[index];
              const subsidyPrice = pricesAfterSubsidy[index];
              
              const diff = originalPrice - norgespris;
              const norgesprisLine = `${diff > 0 ? '+' : ''}${diff.toFixed(2)} kr ${diff > 0 ? 'over' : 'under'} Norgespris`;

              // Only show subsidy price if it's different from original
              if (subsidyPrice !== originalPrice && showSubsidy) {
                return [`${originalPrice.toFixed(2)} → ${subsidyPrice.toFixed(2)} kr/kWh`, norgesprisLine];
              }
              return [`${originalPrice.toFixed(2)} kr/kWh`, norgesprisLine];
            }
            
            // Don't show label for subsidy and Norgespris datasets
            return null;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: !isMobile,
          text: 'NOK per kWh'
        },
        min: isMobile ? Math.min(minPrice, norgespris) * 0.95 : 0, // Start slightly below minimum price (or Norgespris) on mobile
        ticks: {
          stepSize: 0.2,
          font: {
            size: isMobile ? 10 : 11
          },
          callback: function (value: number) {
            return value.toFixed(2);
          }
        }
      },
      x: {
        ticks: {
          font: {
            size: isMobile ? 9 : 11
          },
          maxRotation: isMobile ? 90 : 0,
          minRotation: isMobile ? 90 : 0,
          autoSkip: true,
          autoSkipPadding: isMobile ? 2 : 10,
          callback: function(value: any, index: number) {
            // On mobile, show every 2 hours
            if (isMobile) {
              if (index % 2 === 0 || index === prices.length - 1 || index === currentHourIndex) {
                return labels[index];
              }
              return '';
            }
            return labels[index];
          }
        },
        grid: {
          display: !isMobile
        }
      }
    },
    onResize: (chart: any, size: { width: number, height: number }) => {
      if (window.innerWidth < 768) {
        chart.options.aspectRatio = 1.2;
      } else {
        chart.options.aspectRatio = 2;
      }
    }
  };

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 sm:mb-0">Prisutvikling</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
            Oversikt over strømprisene time for time
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2 mt-3 sm:mt-0 w-full sm:w-auto justify-end">
          {format(selectedDate, 'yyyy-MM-dd') !== format(new Date(), 'yyyy-MM-dd') && (
            <button
              onClick={() => onDateChange(new Date())}
              className="text-xs px-3 py-1.5 rounded-full flex items-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 whitespace-nowrap"
            >
              <ArrowLeft className="w-3 h-3 mr-1" />
              Idag
            </button>
          )}
          
          {format(selectedDate, 'yyyy-MM-dd') !== format(new Date(Date.now() + 24 * 60 * 60 * 1000), 'yyyy-MM-dd') && (
            <button
              onClick={() => onDateChange(new Date(selectedDate.getTime() + 24 * 60 * 60 * 1000))}
              className="text-xs px-3 py-1.5 rounded-full flex items-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ArrowRight className="w-3 h-3 mr-1" />
              Imorgen
            </button>
          )}
          
          <button 
            onClick={() => setShowHistorical(!showHistorical)}
            className={`text-xs px-3 py-1.5 rounded-full flex items-center border whitespace-nowrap ${
              showHistorical 
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <ArrowUpDown className="w-3 h-3 mr-1" />
            {showHistorical ? 'Skjul i fjor' : 'I fjor'}
          </button>
          
          <button 
            onClick={() => setShowNorgespris(!showNorgespris)}
            className={`text-xs px-3 py-1.5 rounded-full flex items-center border whitespace-nowrap ${
              showNorgespris 
                ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-800'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <span className="w-3 h-0.5 mr-1.5 rounded" style={{ backgroundColor: NORGESPRIS_COLOR }} />
            {showNorgespris ? 'Skjul Norgespris' : 'Vis Norgespris'}
          </button>
          
          <button 
            onClick={() => setShowSubsidy(!showSubsidy)}
            className={`text-xs px-3 py-1.5 rounded-full flex items-center border ${
              showSubsidy 
                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <ArrowUpDown className="w-3 h-3 mr-1" />
            {showSubsidy ? 'Skjul støtte' : 'Vis støtte'}
          </button>
          
          <div className="flex p-1 bg-gray-100 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setChartType('line')}
              className={`p-1.5 rounded ${
                chartType === 'line' 
                  ? 'bg-white dark:bg-gray-700 shadow-sm' 
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              title="Linjediagram"
              aria-label="Vis linjediagram"
            >
              <LineChart className="w-4 h-4" />
            </button>
            <button
              onClick={() => setChartType('bar')}
              className={`p-1.5 rounded ${
                chartType === 'bar' 
                  ? 'bg-white dark:bg-gray-700 shadow-sm' 
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              title="Søylediagram"
              aria-label="Vis søylediagram"
            >
              <BarChart className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Spot price compared with Norgespris */}
      <div className={`mb-4 p-3 rounded-lg border flex flex-col gap-2 ${
        isAboveNorgespris
          ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
          : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
      }`}>
        <div className="flex items-start">
          {isAboveNorgespris ? (
            <TrendingUp className="h-5 w-5 text-orange-600 dark:text-orange-400 mr-2 mt-0.5 flex-shrink-0" />
          ) : (
            <TrendingDown className="h-5 w-5 text-green-600 dark:text-green-400 mr-2 mt-0.5 flex-shrink-0" />
          )}
          <div>
            {currentPrice !== null && currentDiff !== null ? (
              <p className="font-medium text-gray-900 dark:text-white">
                Nå: {currentPrice.toFixed(2)} kr/kWh, {Math.abs(currentDiff).toFixed(2)} kr{' '}
                <span className={isAboveNorgespris ? 'text-orange-700 dark:text-orange-300' : 'text-green-700 dark:text-green-300'}>
                  {isAboveNorgespris ? 'over' : 'under'}
                </span>{' '}
                Norgespris ({norgespris.toFixed(2)} kr)
              </p>
            ) : (
              <p className="font-medium text-gray-900 dark:text-white">
                Norgespris: {norgespris.toFixed(2)} kr/kWh{area === 'NO4' ? ' (uten mva.)' : ' (med mva.)'}
              </p>
            )}
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {hoursAboveNorgespris} av {prices.length} timer er over Norgespris · snittpris {avgPrice.toFixed(2)} kr/kWh
            </p>
          </div>
        </div>
        <Link
          to="/norgespris"
          className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline ml-7"
        >
          Lønner Norgespris seg for deg? →
        </Link>
      </div>

      <div 
        className="h-[300px] sm:h-[400px] bg-white dark:bg-gray-800 rounded-lg p-3 mb-4"
        style={{
          containIntrinsicSize: '0 400px',
          contentVisibility: 'auto'
        }}
      >
        {chartType === 'line' ? (
          <Line data={data} options={{
            ...options,
            scales: {
              ...options.scales,
              y: {
                ...options.scales.y,
                grid: {
                  color: document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                },
                ticks: {
                  ...options.scales.y.ticks,
                  color: document.documentElement.classList.contains('dark') ? '#9ca3af' : undefined
                }
              },
              x: {
                ...options.scales.x,
                grid: {
                  color: document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                },
                ticks: {
                  ...options.scales.x.ticks,
                  color: document.documentElement.classList.contains('dark') ? '#9ca3af' : undefined
                }
              }
            },
            plugins: {
              ...options.plugins,
              legend: {
                ...options.plugins.legend,
                labels: {
                  ...options.plugins.legend.labels,
                  color: document.documentElement.classList.contains('dark') ? '#9ca3af' : undefined
                }
              }
            }
          }} />
        ) : (
          <Bar data={barData} options={{
            ...options,
            scales: {
              ...options.scales,
              y: {
                ...options.scales.y,
                grid: {
                  color: document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                },
                ticks: {
                  ...options.scales.y.ticks,
                  color: document.documentElement.classList.contains('dark') ? '#9ca3af' : undefined
                }
              },
              x: {
                ...options.scales.x,
                grid: {
                  color: document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                },
                ticks: {
                  ...options.scales.x.ticks,
                  color: document.documentElement.classList.contains('dark') ? '#9ca3af' : undefined
                }
              }
            },
            plugins: {
              ...options.plugins,
              legend: {
                ...options.plugins.legend,
                labels: {
                  ...options.plugins.legend.labels,
                  color: document.documentElement.classList.contains('dark') ? '#9ca3af' : undefined
                }
              }
            }
          }} />
        )}
      </div>
      
      {/* Best 3-hour period */}
      <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
          <div>
            <h4 className="font-medium text-gray-800 dark:text-gray-200 flex items-center mb-2">
              <Check className="h-4 w-4 text-green-500 dark:text-green-400 mr-1" />
              Beste 3-timers periode
            </h4>
            <p className="text-base text-green-800 dark:text-green-300 font-medium">
              {format(bestStartTime, 'HH:mm', { locale: nb })} - {format(bestEndTime, 'HH:mm', { locale: nb })}
            </p>
            <p className="text-xs text-green-600 dark:text-green-400">
              Gjennomsnittspris: {bestBlock.avgPrice.toFixed(2)} kr/kWh
            </p>
          </div>
          <div className="mt-2 sm:mt-0 text-sm bg-green-100 dark:bg-green-800 rounded-full px-3 py-1 text-green-800 dark:text-green-200">
            Ideelt for vask og lading
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceChart;